import React, { useEffect, useRef, useState } from 'react';
import { Collapse, Badge } from 'reactstrap';
import { useFormikContext } from 'formik';
import { Input, Select, Switch } from '../../lib/form-field';
import {
  LANDING_COMPONENT_TYPES,
  COMPONENT_LABELS,
  COMPONENTS_WITH_CONSENTS,
  FIELD_NAMES,
  GALLERY_ASPECT_RATIOS,
  GALLERY_IMAGE_FITS,
} from '../LandingPageEditor/landingComponents';
import i18n from '../LandingPageEditor/i18n';
import LANGUAGE_TAGS from '../../config/languageTags';
import ConsentsFieldArray from './ConsentsFieldArray';
import GalleryImagesFieldArray from './GalleryImagesFieldArray';

const { getConsentDefaults, getComponentDefaults } = i18n;

// target="_blank" is required, not a nicety: following the link in the same
// tab would navigate away from the editor and discard any unsaved design.
//
// The two title-position usages sit inside AccordionSection's toggle
// <button> (title is rendered as {title} inside it), so a plain click here
// bubbles up and collapses the section the icon is attached to, on top of
// opening the guide tab. stopPropagation keeps the click local to the link.
const GuideLink = ({ anchor }) => (
  <a
    href={`/landing-guide#${anchor}`}
    target="_blank"
    rel="noopener noreferrer"
    className="text-muted ml-1"
    title="Open the guide"
    onClick={(event) => event.stopPropagation()}>
    <i className="bx bx-help-circle" />
  </a>
);

// Al cambio lingua si riallineano i testi consenso per nome, la nota privacy
// e le copy dei componenti. Le etichette dei campi non entrano: restano vuote
// e il renderer ricade sui default tradotti.
const collectPrefillTargets = (values, previous, language) => {
  const targets = [];
  const previousConsents = getConsentDefaults(previous);
  const nextConsents = getConsentDefaults(language);

  targets.push({
    path: 'landingComponents.shared.privacyNoteText',
    current: values.landingComponents.shared.privacyNoteText,
    previousDefault: previousConsents.privacyNoteText,
    next: nextConsents.privacyNoteText,
  });

  (values.landingComponents.shared.consents || []).forEach((consent, index) => {
    targets.push({
      path: `landingComponents.shared.consents.${index}.text`,
      current: consent.text,
      previousDefault: previousConsents.byName[consent.name],
      next: nextConsents.byName[consent.name],
    });
  });

  const previousComponents = getComponentDefaults(previous);
  const nextComponents = getComponentDefaults(language);
  Object.keys(nextComponents).forEach((type) =>
    Object.keys(nextComponents[type]).forEach((field) =>
      targets.push({
        path: `landingComponents.${type}.${field}`,
        current: (values.landingComponents[type] || {})[field],
        previousDefault: previousComponents[type][field],
        next: nextComponents[type][field],
      }),
    ),
  );

  return targets;
};

// Prefills consent texts and component copy when the language changes. Asks
// before overwriting texts the user has edited (i.e. different from the
// previous language's defaults).
const LanguageConsentPrefill = () => {
  const { values, setFieldValue } = useFormikContext();
  const language = values.landingComponents.shared.language;
  const previousRef = useRef(language);

  useEffect(() => {
    const previous = previousRef.current;
    if (previous === language) return;
    previousRef.current = language;

    const targets = collectPrefillTargets(values, previous, language);
    const edited = targets.some(({ current, previousDefault }) => {
      const value = String(current || '').trim();
      return value && value !== previousDefault;
    });

    if (
      edited &&
      !window.confirm(
        'Replace the edited texts with the defaults for the selected language?',
      )
    ) {
      return;
    }

    // `next` is '' (not undefined) for consent names without a translated
    // default (profilingConsent, customerConsent, customerConsentProfile —
    // see i18n.js DICTIONARIES): those are legal texts the client supplies,
    // so a falsy check is required to actually leave them untouched.
    targets.forEach(({ path, next }) => {
      if (next) setFieldValue(path, next);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  return null;
};

// La descrizione va sotto il campo che spiega, non a metà strada verso il
// successivo: mt-n2 recupera parte del margine inferiore di FormGroup, così
// resta visivamente agganciata al proprio input.
// Etichette per la lista chiusa GALLERY_IMAGE_FITS (dal core generato): una
// entry nuova aggiunta nel repo CF deve poter arrivare qui senza un ternario
// da riscrivere, e senza restare mislabelled in silenzio.
const GALLERY_IMAGE_FIT_LABELS = {
  contain: 'Contain — whole image, bands around it',
  cover: 'Cover — fills the frame, edges cut off',
};

const FieldHelp = ({ children, last }) => (
  <p className={`text-muted small mt-n2 ${last ? 'mb-0' : 'mb-3'}`}>
    {children}
  </p>
);

const AccordionSection = ({ title, count, defaultOpen, children }) => {
  const [open, setOpen] = useState(Boolean(defaultOpen));
  return (
    <div className="border rounded mb-2">
      <button
        type="button"
        className="btn btn-light btn-sm w-100 d-flex justify-content-between align-items-center"
        onClick={() => setOpen(!open)}>
        <span className="font-weight-bold font-size-12">{title}</span>
        <span>
          {count > 1 && (
            <Badge color="info" className="mr-2">
              {count}
            </Badge>
          )}
          <i className={`bx ${open ? 'bx-chevron-up' : 'bx-chevron-down'}`} />
        </span>
      </button>
      <Collapse isOpen={open}>
        <div className="p-2">{children}</div>
      </Collapse>
    </div>
  );
};

const ComponentSettingsPanel = ({ markerCounts }) => {
  const count = (type) => (markerCounts && markerCounts[type]) || 0;
  const hasAny = Object.values(LANDING_COMPONENT_TYPES).some(
    (type) => count(type) > 0,
  );
  // Campi e consensi appartengono ai componenti che raccolgono dati o avviano
  // una chiamata. Una pagina con la sola gallery non deve vederseli chiedere:
  // la stessa distinzione governa la validazione in pubblicazione.
  const collectsData = COMPONENTS_WITH_CONSENTS.some((type) => count(type) > 0);

  if (!hasAny) {
    return (
      <p className="text-muted small mt-3 mb-0">
        Insert a landing component above to configure it.
      </p>
    );
  }

  return (
    <div className="mt-3">
      <AccordionSection title="Language" defaultOpen>
        <LanguageConsentPrefill />
        <Select
          name="landingComponents.shared.language"
          id="landingComponents.shared.language"
          label="Language"
          isMulti={false}
          options={LANGUAGE_TAGS}
        />
      </AccordionSection>

      {collectsData && (
        <>
          <AccordionSection
            title={
              <>
                Form Fields
                <GuideLink anchor="campi" />
              </>
            }
            defaultOpen>
            {FIELD_NAMES.map((field) => (
              <div key={field} className="border-bottom pb-2 mb-2">
                <div className="font-size-12 font-weight-bold mb-1">
                  {field}
                </div>
                {field !== 'phone' && (
                  <div className="d-flex">
                    <div className="mr-4">
                      <Switch
                        name={`landingComponents.shared.fields.${field}.show`}
                        label="Show"
                      />
                    </div>
                    <Switch
                      name={`landingComponents.shared.fields.${field}.required`}
                      label="Required"
                    />
                  </div>
                )}
                <Input
                  type="text"
                  name={`landingComponents.shared.fields.${field}.label`}
                  id={`landingComponents.shared.fields.${field}.label`}
                  label="Label"
                />
              </div>
            ))}
            <p className="text-muted small mb-0">
              Phone is always shown and always required. An empty label falls
              back to the translation for the selected language.
            </p>
          </AccordionSection>

          <AccordionSection
            title={
              <>
                Consents & Privacy
                <GuideLink anchor="consensi" />
              </>
            }
            defaultOpen>
            <ConsentsFieldArray />
            <Input
              type="textarea"
              rows="3"
              name="landingComponents.shared.privacyNoteText"
              id="landingComponents.shared.privacyNoteText"
              label="Privacy Note Text"
            />
            <p className="text-muted small mb-0">
              Links use <code>[label](https://…)</code>. See the guide for
              details.
              <GuideLink anchor="link" />
            </p>
          </AccordionSection>
        </>
      )}

      {count(LANDING_COMPONENT_TYPES.leadForm) > 0 && (
        <AccordionSection
          title={COMPONENT_LABELS[LANDING_COMPONENT_TYPES.leadForm]}
          count={count(LANDING_COMPONENT_TYPES.leadForm)}
          defaultOpen>
          <Input
            type="text"
            name="landingComponents.lead_form.buttonLabel"
            id="landingComponents.lead_form.buttonLabel"
            label="Button Label"
          />
          <Input
            type="text"
            name="landingComponents.lead_form.successMessage"
            id="landingComponents.lead_form.successMessage"
            label="Success Message"
          />
          <Input
            type="color"
            name="landingComponents.lead_form.primaryColor"
            id="landingComponents.lead_form.primaryColor"
            label="Primary Color"
          />
        </AccordionSection>
      )}

      {count(LANDING_COMPONENT_TYPES.callbackOrCall) > 0 && (
        <AccordionSection
          title={COMPONENT_LABELS[LANDING_COMPONENT_TYPES.callbackOrCall]}
          count={count(LANDING_COMPONENT_TYPES.callbackOrCall)}
          defaultOpen>
          <Input
            type="text"
            name="landingComponents.callback_or_call.choiceTitle"
            id="landingComponents.callback_or_call.choiceTitle"
            label="Choice Title"
          />
          <Input
            type="text"
            name="landingComponents.callback_or_call.callbackButtonLabel"
            id="landingComponents.callback_or_call.callbackButtonLabel"
            label="Callback Button Label"
          />
          <Input
            type="text"
            name="landingComponents.callback_or_call.callButtonLabel"
            id="landingComponents.callback_or_call.callButtonLabel"
            label="Call Button Label"
          />
          <Input
            type="text"
            name="landingComponents.callback_or_call.phoneNumber"
            id="landingComponents.callback_or_call.phoneNumber"
            label="Phone Number"
          />
          <Input
            type="text"
            name="landingComponents.callback_or_call.backLabel"
            id="landingComponents.callback_or_call.backLabel"
            label="Back Button Label"
          />
          <Input
            type="text"
            name="landingComponents.callback_or_call.successMessage"
            id="landingComponents.callback_or_call.successMessage"
            label="Success Message"
          />
          <Input
            type="color"
            name="landingComponents.callback_or_call.primaryColor"
            id="landingComponents.callback_or_call.primaryColor"
            label="Primary Color"
          />
        </AccordionSection>
      )}

      {count(LANDING_COMPONENT_TYPES.clickToCall) > 0 && (
        <AccordionSection
          title={COMPONENT_LABELS[LANDING_COMPONENT_TYPES.clickToCall]}
          count={count(LANDING_COMPONENT_TYPES.clickToCall)}
          defaultOpen>
          <Input
            type="text"
            name="landingComponents.click_to_call.title"
            id="landingComponents.click_to_call.title"
            label="Title"
          />
          <Input
            type="text"
            name="landingComponents.click_to_call.buttonLabel"
            id="landingComponents.click_to_call.buttonLabel"
            label="Button Label"
          />
          <Input
            type="text"
            name="landingComponents.click_to_call.phoneNumber"
            id="landingComponents.click_to_call.phoneNumber"
            label="Phone Number"
          />
          <Input
            type="color"
            name="landingComponents.click_to_call.primaryColor"
            id="landingComponents.click_to_call.primaryColor"
            label="Primary Color"
          />
        </AccordionSection>
      )}

      {count(LANDING_COMPONENT_TYPES.gallery) > 0 && (
        <AccordionSection
          title={
            <>
              {COMPONENT_LABELS[LANDING_COMPONENT_TYPES.gallery]}
              <GuideLink anchor="gallery" />
            </>
          }
          count={count(LANDING_COMPONENT_TYPES.gallery)}
          defaultOpen>
          <GalleryImagesFieldArray />
          <Select
            name="landingComponents.gallery.aspectRatio"
            id="landingComponents.gallery.aspectRatio"
            label="Aspect Ratio"
            isMulti={false}
            options={GALLERY_ASPECT_RATIOS.map((ratio) => ({
              value: ratio,
              label: ratio,
            }))}
          />
          <FieldHelp>Same shape and height for every slide.</FieldHelp>
          <Input
            type="color"
            name="landingComponents.gallery.backgroundColor"
            id="landingComponents.gallery.backgroundColor"
            label="Background Color"
          />
          <FieldHelp>
            Fills the space around images that don&apos;t match the ratio
            (visible with Contain).
          </FieldHelp>
          <Select
            name="landingComponents.gallery.imageFit"
            id="landingComponents.gallery.imageFit"
            label="Image Fit"
            isMulti={false}
            options={GALLERY_IMAGE_FITS.map((fit) => ({
              value: fit,
              label: GALLERY_IMAGE_FIT_LABELS[fit] || fit,
            }))}
          />
          <FieldHelp>
            Applies to images without a crop. Cropped images always fill the
            frame.
          </FieldHelp>
          <Input
            type="color"
            name="landingComponents.gallery.primaryColor"
            id="landingComponents.gallery.primaryColor"
            label="Primary Color"
          />
          <FieldHelp last>Dot marking the current image.</FieldHelp>
        </AccordionSection>
      )}
    </div>
  );
};

export default ComponentSettingsPanel;
